// License-key lifecycle: list, issue a replacement, revoke. Lives on the
// Settings screen (frame 18), which draws no such card — this closes a real
// product gap rather than a drawn one, so it is deliberately a plain Card in
// the existing chassis and not an attempt at pixel convergence.
//
// SPEC NOTE, the same discipline `InstallScreen.tsx` applies to frame 17's
// key reveal: the ONLY key this component ever renders is the one the mint
// mutation just returned. Every other row is a fingerprint. There is no
// masked field, no "reveal" toggle, and no entry box — a key is unrecoverable
// after issuance because only its sha256 is stored, and there is no scenario
// in which a human types one back into our own dashboard (the session cookie
// is the human credential; the key is the machine one).
//
// Rotation is mint-then-revoke, two deliberate steps, because revoking at
// mint time would break the customer's live embed in the window before they
// have pasted the new snippet. The revoke confirm therefore names the
// consequence, and names it more sharply when it is the last active key.

import { useState } from "react";
import { Badge, Button, Card, EmptyState, ErrorState, Skeleton, Table } from "../../ui";
import { CopyField } from "./CopyField";
import {
  useLicenseKeys,
  useMintLicenseKey,
  useRevokeLicenseKey,
  type LicenseKey,
} from "./api";
import "./ops.css";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  // Naive-UTC from DuckDB (no offset, no Z) — parsed as local by Date, which
  // is fine for a date-only display and wrong for anything finer, so this
  // renders a date and never a time.
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toISOString().slice(0, 10);
}

export function LicenseKeysCard() {
  const keys = useLicenseKeys();
  const mint = useMintLicenseKey();
  const revoke = useRevokeLicenseKey();

  // The plaintext key, held here and nowhere else. Not cached, not persisted,
  // gone on unmount — see the header note.
  const [minted, setMinted] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rows = keys.data ?? [];
  const activeCount = rows.filter((row) => row.status === "active").length;

  function issue() {
    setError(null);
    mint.mutate(undefined, {
      onSuccess: (result) => setMinted(result.key),
      onError: (err) =>
        setError(
          err.status === 409
            ? "Your organisation has no active paid licence to issue a key against. Contact support."
            : err.message || "Couldn't issue a key.",
        ),
    });
  }

  function confirmRevoke(keyId: string) {
    setError(null);
    revoke.mutate(keyId, {
      onSuccess: () => setConfirming(null),
      onError: (err) => setError(err.message || "Couldn't revoke that key."),
    });
  }

  const columns = [
    {
      key: "key_id",
      header: "Key ID",
      cell: (row: LicenseKey) => <span className="type-code">{row.key_id}</span>,
    },
    { key: "plan", header: "Plan", cell: (row: LicenseKey) => row.plan },
    {
      key: "domains",
      header: "Domains",
      cell: (row: LicenseKey) => row.domains.join(", ") || "—",
    },
    {
      key: "created_at",
      header: "Issued",
      cell: (row: LicenseKey) => formatDate(row.created_at),
    },
    {
      key: "status",
      header: "Status",
      cell: (row: LicenseKey) => (
        <Badge tone={row.status === "active" ? "nominal" : "neutral"} lamp>
          {row.status === "active" ? row.resolved_state : "revoked"}
        </Badge>
      ),
    },
    {
      key: "revoke",
      header: "",
      cell: (row: LicenseKey) =>
        row.status !== "active" ? null : confirming === row.key_id ? (
          <span className="maisight-ops-key-confirm">
            <Button
              variant="danger"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => confirmRevoke(row.key_id)}
            >
              Confirm
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
          </span>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setConfirming(row.key_id)}>
            Revoke
          </Button>
        ),
    },
  ];

  return (
    <Card header="License keys">
      <p className="type-bodyS maisight-ops-card-note">
        A license key is what your docs site sends as{" "}
        <span className="type-code">data-maisight-key</span> — it identifies your
        site to mAiSight, not you. Signing in here is a separate credential. Keys
        are stored hashed, so we show a fingerprint and can never display a key
        again after it is issued.
      </p>

      {minted ? (
        <div className="maisight-ops-banner" data-tone="neutral" role="status">
          <div className="maisight-ops-banner-body">
            <p className="type-bodyS">
              <strong>Copy this now.</strong> This is the only time this key will be
              shown — we store only its hash and cannot display it again. Paste it
              into your embed snippet, confirm your site still works, then revoke the
              old key below.
            </p>
            <CopyField label="New license key" value={minted} />
            <div className="maisight-ops-actions">
              <Button variant="secondary" onClick={() => setMinted(null)}>
                I've saved it
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="maisight-ops-banner" data-tone="danger" role="alert">
          <p className="type-bodyS maisight-ops-banner-body">{error}</p>
        </div>
      ) : null}

      {keys.isPending ? (
        <Skeleton width={480} height={16} />
      ) : keys.isError ? (
        <ErrorState headline="Couldn't load your license keys" detail={keys.error?.message} />
      ) : (
        <Table
          label="License keys"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.key_id}
          unit="keys"
          empty={
            <EmptyState
              headline="No license keys yet"
              body="Issue one to embed mAiSight on your docs site."
              icon="settings"
            />
          }
        />
      )}

      {confirming && activeCount === 1 ? (
        <p className="type-bodyS maisight-ops-key-warning" role="alert">
          This is your only active key. Revoking it stops mAiSight rendering on your
          site, and you'll need to contact support for a replacement — issue a new key
          first if you only meant to rotate.
        </p>
      ) : null}

      <div className="maisight-ops-actions">
        <Button variant="primary" disabled={mint.isPending} onClick={issue}>
          {activeCount > 0 ? "Issue a replacement key" : "Issue a key"}
        </Button>
      </div>
    </Card>
  );
}
